import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X, Loader2, Eye, EyeOff, Image as ImageIcon, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import ImageUploader from "../../components/ImageUploader";

const EMPTY = {
    title: "",
    subtitle: "",
    image_url: "",
    image_only: false,
    background_color: "#5B21B6",
    text_color: "#FFFFFF",
    cta_label: "Order Now",
    cta_type: "NONE",
    cta_target: "",
    priority: 0,
    enabled: true,
    zone_ids: [],
    // iter101 — placement targeting (Home / Vertical / Shop). Multi-select;
    // banners can appear on multiple placements simultaneously. Scope
    // arrays are optional filters within each placement — empty = "all".
    placements: ["HOME"],
    business_category_ids: [],
    restaurant_ids: [],
};

const PLACEMENT_OPTIONS = [
    { key: "HOME", label: "Home page", hint: "Above the main feed" },
    { key: "VERTICAL", label: "Vertical page", hint: "Business-category listing" },
    { key: "SHOP", label: "Shop page", hint: "Individual restaurant detail" },
];

const CTA_TYPES = [
    { key: "NONE", label: "No action (display only)" },
    { key: "RESTAURANT", label: "Open a shop" },
    { key: "EXTERNAL", label: "External URL" },
    { key: "SEARCH", label: "Run a search" },
    { key: "COUPON", label: "Open offers page" },
];

export default function AdminBanners() {
    const [rows, setRows] = useState([]);
    const [restaurants, setRestaurants] = useState([]);
    const [zones, setZones] = useState([]);
    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [edit, setEdit] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);

    const load = () => api.get("/admin/banners").then(({ data }) => setRows(data));
    useEffect(() => {
        load();
        api.get("/admin/restaurants").then(({ data }) => setRestaurants(data || []));
        api.get("/admin/zones").then(({ data }) => setZones((data || []).filter(z => z.status !== "DELETED")));
        // iter101 — verticals used to scope VERTICAL-placement banners.
        api.get("/admin/business-categories").then(({ data }) => setCategories(data || [])).catch(() => setCategories([]));
    }, []);

    const openCreate = () => { setEdit(null); setForm(EMPTY); setOpen(true); };
    const openEdit = (b) => {
        setEdit(b);
        setForm({
            ...EMPTY, ...b,
            cta_target: b.cta_target || "",
            zone_ids: Array.isArray(b.zone_ids) ? b.zone_ids : [],
            // iter101 — placement + scope defaults for legacy banners.
            placements: Array.isArray(b.placements) && b.placements.length ? b.placements : ["HOME"],
            business_category_ids: Array.isArray(b.business_category_ids) ? b.business_category_ids : [],
            restaurant_ids: Array.isArray(b.restaurant_ids) ? b.restaurant_ids : [],
        });
        setOpen(true);
    };

    const save = async () => {
        if (!form.title.trim()) return toast.error("Title is required");
        if (!Array.isArray(form.placements) || form.placements.length === 0) {
            return toast.error("Pick at least one placement (Home / Vertical / Shop)");
        }
        setBusy(true);
        try {
            const payload = {
                ...form,
                priority: Number(form.priority) || 0,
                zone_ids: form.zone_ids || [],
                placements: form.placements || ["HOME"],
                business_category_ids: form.business_category_ids || [],
                restaurant_ids: form.restaurant_ids || [],
            };
            if (edit) await api.patch(`/admin/banners/${edit.id}`, payload);
            else await api.post(`/admin/banners`, payload);
            toast.success(edit ? "Banner updated" : "Banner created");
            setOpen(false); load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
    };

    const toggle = async (b) => {
        await api.patch(`/admin/banners/${b.id}`, { enabled: !b.enabled });
        load();
    };
    const remove = async (b) => {
        if (!confirm(`Delete banner "${b.title}"? Click-history will be retained.`)) return;
        try {
            const { data } = await api.delete(`/admin/banners/${b.id}`);
            toast.success(data?.mode === "archived" ? "Archived — banner hidden, history kept" : "Deleted");
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    };
    const shiftPriority = async (b, delta) => {
        await api.patch(`/admin/banners/${b.id}`, { priority: (b.priority || 0) + delta });
        load();
    };

    return (
        <div>
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-bold">Home Banners</h1>
                    <p className="text-sm text-muted-foreground mt-1">Hero carousel shown above the restaurant feed. Higher priority appears first.</p>
                </div>
                <button data-testid="add-banner" onClick={openCreate} className="bg-primary text-primary-foreground rounded-xl px-4 py-2 font-semibold text-sm inline-flex items-center gap-2"><Plus size={16} /> New banner</button>
            </div>

            {/* Live preview */}
            <div className="mt-6">
                <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Preview (customer home)</p>
                <div className="flex gap-3 overflow-x-auto no-scrollbar pb-2">
                    {rows.filter(r => r.enabled).slice(0, 6).map((b) => <BannerCard key={b.id} b={b} />)}
                    {rows.filter(r => r.enabled).length === 0 && <p className="text-xs text-muted-foreground">No enabled banners — the home page will hide this section.</p>}
                </div>
            </div>

            <div className="mt-6 bg-card border border-border rounded-2xl overflow-hidden">
                {/* Mobile + tablet portrait (<lg): stacked card view */}
                <div className="lg:hidden">
                    {rows.length === 0 && <div className="text-center py-10 text-muted-foreground text-sm">No banners yet — tap <strong>New banner</strong>.</div>}
                    {rows.map((b) => (
                        <div key={b.id} data-testid={`banner-card-${b.id}`} className="border-t border-border first:border-t-0 px-4 py-3">
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-sm truncate">{b.title}</p>
                                    <p className="text-[11px] text-muted-foreground line-clamp-1">{b.subtitle || "—"}</p>
                                </div>
                                <button data-testid={`banner-toggle-card-${b.id}`} onClick={() => toggle(b)} className={`shrink-0 text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${b.enabled ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                                    {b.enabled ? <><Eye size={11} /> On</> : <><EyeOff size={11} /> Off</>}
                                </button>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1">
                                {((b.placements && b.placements.length) ? b.placements : ["HOME"]).map((p) => (
                                    <span key={p} data-testid={`banner-placement-card-${b.id}-${p}`} className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700">{p}</span>
                                ))}
                                {(b.zone_ids || []).length === 0 ? (
                                    <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">All zones</span>
                                ) : (b.zone_ids || []).map((zid) => (
                                    <span key={zid} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                                        {zones.find((z) => z.id === zid)?.name || zid}
                                    </span>
                                ))}
                            </div>
                            <div className="mt-2 flex items-center justify-between gap-2">
                                <div className="text-[11px] text-muted-foreground min-w-0 truncate">
                                    {b.cta_type === "NONE" && "No CTA"}
                                    {b.cta_type === "EXTERNAL" && <span className="inline-flex items-center gap-1"><ExternalLink size={11} />{b.cta_target?.slice(0, 24)}…</span>}
                                    {b.cta_type === "RESTAURANT" && <span>{restaurants.find(r => r.id === b.cta_target)?.name || b.cta_target}</span>}
                                    {b.cta_type === "SEARCH" && <span>Search · {b.cta_target}</span>}
                                    {b.cta_type === "COUPON" && <span>Offers</span>}
                                </div>
                                <div className="inline-flex items-center gap-1 shrink-0">
                                    <button onClick={() => shiftPriority(b, -1)} className="px-1.5 text-sm">↓</button>
                                    <span className="font-bold min-w-[20px] text-center text-xs">{b.priority || 0}</span>
                                    <button onClick={() => shiftPriority(b, +1)} className="px-1.5 text-sm">↑</button>
                                    <button data-testid={`banner-edit-card-${b.id}`} onClick={() => openEdit(b)} className="touch-target rounded-lg hover:bg-muted"><Pencil size={16} /></button>
                                    <button data-testid={`banner-del-card-${b.id}`} onClick={() => remove(b)} className="touch-target rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={16} /></button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Desktop (lg+): classic table */}
                <div className="hidden lg:block overflow-x-auto">
                    <table className="w-full text-sm min-w-[900px]">
                        <thead className="bg-muted/60 text-left text-[11px] uppercase tracking-wider">
                            {/* iter146 — Action column is sticky so Edit/Delete
                                stay reachable without scrolling all the way
                                right on narrower desktop/laptop widths. */}
                            <tr><th className="px-4 py-3">Banner</th><th className="px-4 py-3">CTA</th><th className="px-4 py-3">Priority</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right sticky right-0 bg-muted/60 border-l border-border">Action</th></tr>
                        </thead>
                        <tbody>
                            {rows.map((b) => (
                                <tr key={b.id} data-testid={`banner-row-${b.id}`} className="border-t border-border">
                                    <td className="px-4 py-3">
                                        <p className="font-semibold">{b.title}</p>
                                        <p className="text-xs text-muted-foreground line-clamp-1">{b.subtitle || "—"}</p>
                                        {/* iter101 — placement badges (Home / Vertical / Shop). */}
                                        <div className="mt-1 flex flex-wrap gap-1">
                                            {((b.placements && b.placements.length) ? b.placements : ["HOME"]).map((p) => (
                                                <span
                                                    key={p}
                                                    data-testid={`banner-placement-${b.id}-${p}`}
                                                    className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700"
                                                >{p}</span>
                                            ))}
                                            {(b.zone_ids || []).length === 0 ? (
                                                <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-muted text-muted-foreground">All zones</span>
                                            ) : (
                                                (b.zone_ids || []).map((zid) => (
                                                    <span key={zid} className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">
                                                        {zones.find((z) => z.id === zid)?.name || zid}
                                                    </span>
                                                ))
                                            )}
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-xs">
                                        {b.cta_type === "NONE" && "—"}
                                        {b.cta_type === "EXTERNAL" && <span className="inline-flex items-center gap-1"><ExternalLink size={11} />{b.cta_target?.slice(0, 28)}…</span>}
                                        {b.cta_type === "RESTAURANT" && <span>{restaurants.find(r => r.id === b.cta_target)?.name || b.cta_target}</span>}
                                        {b.cta_type === "SEARCH" && <span>Search · {b.cta_target}</span>}
                                        {b.cta_type === "COUPON" && <span>Offers</span>}
                                    </td>
                                    <td className="px-4 py-3">
                                        <div className="inline-flex items-center gap-1">
                                            <button onClick={() => shiftPriority(b, -1)} className="px-1.5">↓</button>
                                            <span className="font-bold min-w-[20px] text-center">{b.priority || 0}</span>
                                            <button onClick={() => shiftPriority(b, +1)} className="px-1.5">↑</button>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3">
                                        <button data-testid={`banner-toggle-${b.id}`} onClick={() => toggle(b)} className={`text-[10px] uppercase tracking-wider font-bold px-2 py-1 rounded-full inline-flex items-center gap-1 ${b.enabled ? "bg-emerald-100 text-emerald-700" : "bg-muted text-muted-foreground"}`}>
                                            {b.enabled ? <><Eye size={11} /> Visible</> : <><EyeOff size={11} /> Hidden</>}
                                        </button>
                                    </td>
                                    <td className="px-4 py-3 text-right sticky right-0 bg-card border-l border-border">
                                        {/* iter146 — .touch-target (44×44 min) replaces the old
                                            `p-2` (~30×30) buttons — too small to see/tap reliably
                                            at narrower table widths (user-reported). */}
                                        <div className="inline-flex items-center gap-1">
                                            <button data-testid={`banner-edit-${b.id}`} onClick={() => openEdit(b)} className="touch-target rounded-lg hover:bg-muted"><Pencil size={16} /></button>
                                            <button data-testid={`banner-del-${b.id}`} onClick={() => remove(b)} className="touch-target rounded-lg text-destructive hover:bg-destructive/10"><Trash2 size={16} /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-muted-foreground">No banners yet — click <strong>New banner</strong> to add one.</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            {open && (
                <BannerEditor form={form} setForm={setForm} restaurants={restaurants} zones={zones} categories={categories} onClose={() => setOpen(false)} onSave={save} busy={busy} edit={edit} />
            )}
        </div>
    );
}

function BannerEditor({ form, setForm, restaurants, zones, categories, onClose, onSave, busy, edit }) {
    const toggleZone = (zid) => {
        const cur = form.zone_ids || [];
        const next = cur.includes(zid) ? cur.filter((x) => x !== zid) : [...cur, zid];
        setForm({ ...form, zone_ids: next });
    };
    // iter101 — placement multi-select + optional scope narrowing.
    const togglePlacement = (key) => {
        const cur = form.placements || ["HOME"];
        const next = cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key];
        setForm({ ...form, placements: next.length ? next : ["HOME"] });
    };
    const toggleCategory = (cid) => {
        const cur = form.business_category_ids || [];
        const next = cur.includes(cid) ? cur.filter((x) => x !== cid) : [...cur, cid];
        setForm({ ...form, business_category_ids: next });
    };
    const toggleRestaurant = (rid) => {
        const cur = form.restaurant_ids || [];
        const next = cur.includes(rid) ? cur.filter((x) => x !== rid) : [...cur, rid];
        setForm({ ...form, restaurant_ids: next });
    };
    const showsVertical = (form.placements || []).includes("VERTICAL");
    const showsShop = (form.placements || []).includes("SHOP");
    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-card rounded-2xl p-5 w-full max-w-3xl max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-lg">{edit ? `Edit banner · ${edit.title}` : "New banner"}</h3>
                    <button onClick={onClose}><X size={20} /></button>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    <div>
                        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mb-2">Live preview</p>
                        <BannerCard b={form} />
                        <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground mt-4 mb-2">Quick palettes</p>
                        <div className="flex flex-wrap gap-2">
                            {[
                                { bg: "#5B21B6", text: "#FFFFFF" },
                                { bg: "#0F766E", text: "#FFFFFF" },
                                { bg: "#0EA5E9", text: "#FFFFFF" },
                                { bg: "#DC2626", text: "#FFFFFF" },
                                { bg: "#F59E0B", text: "#1F2937" },
                                { bg: "#1F2937", text: "#FFFFFF" },
                            ].map((p) => (
                                <button key={p.bg} onClick={() => setForm({ ...form, background_color: p.bg, text_color: p.text })}
                                    className="w-7 h-7 rounded-full border border-border" style={{ background: p.bg }} title={p.bg} />
                            ))}
                        </div>
                    </div>

                    <div className="space-y-3 text-sm">
                        <Lbl label="Title"><input data-testid="banner-title" className="inp" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></Lbl>
                        <Lbl label="Subtitle"><input className="inp" value={form.subtitle || ""} onChange={(e) => setForm({ ...form, subtitle: e.target.value })} /></Lbl>
                        <Lbl label={<span className="inline-flex items-center gap-1"><ImageIcon size={12} /> Banner image (optional)</span>}>
                            <ImageUploader
                                value={form.image_url}
                                onUploaded={(url) => setForm({ ...form, image_url: url })}
                                aspect="aspect-[16/9]"
                                testId="banner-image"
                            />
                            {/* iter134f — full-image (image-only) toggle */}
                            <label className="mt-2 flex items-start gap-2 text-xs cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    data-testid="banner-image-only"
                                    checked={!!form.image_only}
                                    onChange={(e) => setForm({ ...form, image_only: e.target.checked })}
                                    disabled={!form.image_url}
                                    className="mt-0.5"
                                />
                                <span className={form.image_url ? "" : "opacity-60"}>
                                    <strong>Use uploaded image as the full banner</strong>
                                    <span className="block text-muted-foreground mt-0.5">
                                        Hides the title / subtitle / CTA text and shows only your image edge-to-edge. Ideal for pre-designed promo images from Canva / Figma. Recommended size: <strong>1600×750px</strong> (16:7.5 aspect).
                                    </span>
                                </span>
                            </label>
                        </Lbl>
                        <div className="grid grid-cols-2 gap-3">
                            <Lbl label="Background"><input type="color" className="inp h-10 p-1" value={form.background_color} onChange={(e) => setForm({ ...form, background_color: e.target.value })} /></Lbl>
                            <Lbl label="Text"><input type="color" className="inp h-10 p-1" value={form.text_color} onChange={(e) => setForm({ ...form, text_color: e.target.value })} /></Lbl>
                        </div>
                        <Lbl label="CTA label">
                            <input className="inp" value={form.cta_label || ""} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} placeholder="e.g. Order Now" />
                        </Lbl>
                        <Lbl label="CTA action">
                            <select className="inp" value={form.cta_type} onChange={(e) => setForm({ ...form, cta_type: e.target.value, cta_target: "" })}>
                                {CTA_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                            </select>
                        </Lbl>
                        {form.cta_type === "RESTAURANT" && (
                            <Lbl label="Shop">
                                <select data-testid="banner-restaurant" className="inp" value={form.cta_target || ""} onChange={(e) => setForm({ ...form, cta_target: e.target.value })}>
                                    <option value="">Select shop…</option>
                                    {restaurants.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                                </select>
                            </Lbl>
                        )}
                        {form.cta_type === "EXTERNAL" && (
                            <Lbl label="External URL"><input className="inp" value={form.cta_target || ""} onChange={(e) => setForm({ ...form, cta_target: e.target.value })} placeholder="https://example.com" /></Lbl>
                        )}
                        {form.cta_type === "SEARCH" && (
                            <Lbl label="Search query"><input className="inp" value={form.cta_target || ""} onChange={(e) => setForm({ ...form, cta_target: e.target.value })} placeholder="e.g. Biryani" /></Lbl>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <Lbl label="Priority"><input type="number" className="inp" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} /></Lbl>
                            <Lbl label="Visibility">
                                <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-1">
                                    <input type="checkbox" checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} className="accent-primary w-4 h-4" />
                                    <span>Enabled (shown on home)</span>
                                </label>
                            </Lbl>
                        </div>

                        <Lbl label="Placements">
                            <p className="text-xs text-muted-foreground -mt-1 mb-1">
                                {/* iter101 — where the banner appears. Multi-select. */}
                                Pick where this banner should appear. Multi-select allowed — one banner can run on Home + a specific vertical + a shop.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {PLACEMENT_OPTIONS.map((opt) => {
                                    const on = (form.placements || []).includes(opt.key);
                                    return (
                                        <button
                                            key={opt.key}
                                            type="button"
                                            data-testid={`banner-placement-${opt.key}`}
                                            onClick={() => togglePlacement(opt.key)}
                                            className={`text-xs font-semibold px-3 py-2 rounded-xl border text-left inline-flex flex-col gap-0.5 ${on ? "bg-primary text-primary-foreground border-primary" : "border-border bg-card"}`}
                                        >
                                            <span className="inline-flex items-center gap-1">{on && <span aria-hidden>✓</span>}{opt.label}</span>
                                            <span className="text-[9px] opacity-80">{opt.hint}</span>
                                        </button>
                                    );
                                })}
                            </div>
                        </Lbl>

                        {showsVertical && (
                            <Lbl label="Vertical scope (optional)">
                                <p className="text-xs text-muted-foreground -mt-1 mb-1">
                                    Empty = show on every vertical. Pick one or more to narrow the VERTICAL placement to those verticals only.
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    <button
                                        type="button"
                                        data-testid="banner-vertical-all"
                                        onClick={() => setForm({ ...form, business_category_ids: [] })}
                                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${(!form.business_category_ids || form.business_category_ids.length === 0) ? "bg-foreground text-background border-foreground" : "border-border"}`}
                                    >All verticals</button>
                                    {(categories || []).map((c) => {
                                        const on = (form.business_category_ids || []).includes(c.id);
                                        return (
                                            <button
                                                key={c.id}
                                                type="button"
                                                data-testid={`banner-vertical-${c.id}`}
                                                onClick={() => toggleCategory(c.id)}
                                                className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1 ${on ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                                            >
                                                {on && <span aria-hidden>✓</span>}{c.name}
                                            </button>
                                        );
                                    })}
                                    {(!categories || categories.length === 0) && <p className="text-xs text-muted-foreground">No verticals configured yet.</p>}
                                </div>
                            </Lbl>
                        )}

                        {showsShop && (
                            <Lbl label="Shop scope (optional)">
                                <p className="text-xs text-muted-foreground -mt-1 mb-1">
                                    Empty = show on every shop&apos;s detail page. Pick one or more to narrow the SHOP placement to those shops only.
                                </p>
                                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                                    <button
                                        type="button"
                                        data-testid="banner-shop-all"
                                        onClick={() => setForm({ ...form, restaurant_ids: [] })}
                                        className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${(!form.restaurant_ids || form.restaurant_ids.length === 0) ? "bg-foreground text-background border-foreground" : "border-border"}`}
                                    >All shops</button>
                                    {(restaurants || []).map((r) => {
                                        const on = (form.restaurant_ids || []).includes(r.id);
                                        return (
                                            <button
                                                key={r.id}
                                                type="button"
                                                data-testid={`banner-shop-${r.id}`}
                                                onClick={() => toggleRestaurant(r.id)}
                                                className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1 ${on ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                                            >
                                                {on && <span aria-hidden>✓</span>}{r.name}
                                            </button>
                                        );
                                    })}
                                </div>
                            </Lbl>
                        )}

                        <Lbl label="Zone targeting">
                            <p className="text-xs text-muted-foreground -mt-1 mb-1">
                                Leave empty to show in every zone. Select one or more zones to restrict this banner to customers in those zones only.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <button
                                    type="button"
                                    data-testid="banner-zone-all"
                                    onClick={() => setForm({ ...form, zone_ids: [] })}
                                    className={`text-xs font-semibold px-3 py-1.5 rounded-full border ${(!form.zone_ids || form.zone_ids.length === 0) ? "bg-foreground text-background border-foreground" : "border-border"}`}
                                >
                                    All zones
                                </button>
                                {zones.map((z) => {
                                    const on = (form.zone_ids || []).includes(z.id);
                                    return (
                                        <button
                                            key={z.id}
                                            type="button"
                                            data-testid={`banner-zone-${z.id}`}
                                            onClick={() => toggleZone(z.id)}
                                            className={`text-xs font-semibold px-3 py-1.5 rounded-full border inline-flex items-center gap-1 ${on ? "bg-primary text-primary-foreground border-primary" : "border-border"}`}
                                        >
                                            {on && <span aria-hidden>✓</span>}{z.name}
                                        </button>
                                    );
                                })}
                                {zones.length === 0 && <p className="text-xs text-muted-foreground">No zones configured yet.</p>}
                            </div>
                        </Lbl>
                    </div>
                </div>

                <button data-testid="save-banner" onClick={onSave} disabled={busy || !form.title} className="mt-5 w-full bg-primary text-primary-foreground rounded-xl py-3 font-bold inline-flex items-center justify-center gap-2 disabled:opacity-50">
                    {busy && <Loader2 size={16} className="animate-spin" />} {edit ? "Save changes" : "Create banner"}
                </button>
                <style>{`.inp{background:hsl(var(--muted));border-radius:0.75rem;padding:0.6rem 0.75rem;outline:none;font-size:0.875rem;width:100%}`}</style>
            </div>
        </div>
    );
}

function Lbl({ label, children }) {
    return (
        <label className="flex flex-col gap-1">
            <span className="text-[11px] uppercase tracking-wider font-bold text-muted-foreground">{label}</span>
            {children}
        </label>
    );
}

/** Small static replica of the customer-side banner card so admin can preview live. */
export function BannerCard({ b }) {
    // iter134f — respect the image_only toggle in the preview so admins
    // see exactly what customers will get.
    if (b.image_only && b.image_url) {
        return (
            <div
                className="min-w-[300px] w-[300px] aspect-[16/8] rounded-2xl overflow-hidden relative shadow-sm bg-muted"
            >
                <img
                    src={b.image_url}
                    alt={b.title || "Banner preview"}
                    className="absolute inset-0 w-full h-full object-cover"
                    onError={(e) => { e.target.style.display = "none"; }}
                />
            </div>
        );
    }
    return (
        <div
            className="min-w-[300px] w-[300px] aspect-[16/8] rounded-2xl overflow-hidden flex items-center px-5 relative shadow-sm"
            style={{ background: b.background_color || "#5B21B6", color: b.text_color || "#fff" }}
        >
            <div className="flex-1 z-10">
                <p className="font-bold text-lg leading-tight line-clamp-2">{b.title || "Banner title"}</p>
                {b.subtitle && <p className="text-xs opacity-90 mt-1 line-clamp-1">{b.subtitle}</p>}
                {b.cta_label && b.cta_type !== "NONE" && (
                    <span className="inline-block mt-2 bg-white/95 text-zinc-900 rounded-full text-[11px] font-bold px-3 py-1.5">{b.cta_label}</span>
                )}
            </div>
            {b.image_url && (
                <img src={b.image_url} alt="" className="absolute right-0 top-0 h-full w-1/2 object-cover opacity-90" onError={(e) => { e.target.style.display = "none"; }} />
            )}
        </div>
    );
}
